"use client";

import { type CSSProperties } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Clock, PanelRightOpen, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal, reveal, SceneRoot } from "../scenes/productScene";
import styles from "./dailyDiary.module.scss";
import { DIARY_FILLED, DIARY_UI, DIARY_WEEKS, type DiaryDay, type DiaryTone } from "./dailyDiaryData";

/**
 * HTML rebuild of the daily-diary artwork (Figma 3165:29712): the Daily Diary dashboard with its
 * "start trading" banner and the month calendar of diary entries.
 *
 * Sizes are Figma px ÷ 8 in rem (the 704 × 832 frame is the section's 88rem × 104rem box). The
 * reveal builds the dashboard, then writes the diary in chronologically, one day after another,
 * and ends on today's entry. Only opacity / transform are animated; the two idle loops (today's
 * outline, banner glow) are CSS on opacity and pause off screen.
 */

const T = {
  panel: 0,
  topbar: 0.3,
  banner: 0.45,
  logo: 0.7,
  title: 0.8,
  stats: 0.95,
  heading: 0.6,
  weekdays: 0.8,
  rows: 0.9,
  rowStep: 0.08,
  fill: 1.35,
  fillStep: 0.09,
} as const;
const T_TODAY = T.fill + DIARY_FILLED * T.fillStep + 0.15;

const TONE: Record<DiaryTone, { text: string; rgb: string }> = {
  green: { text: "#32c870", rgb: "50,200,112" },
  orange: { text: "#ff9003", rgb: "255,144,3" },
  red: { text: "#ff5b5b", rgb: "211,64,64" },
};

function TradeStatsMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="9 9 42 43"
      className={className}
      aria-hidden
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M36.546 9V23.0739L23.2745 9H10V23.4631H36.184L23.092 38.1223V52H36.546V23.4631H50V9H36.546Z"
        fill="white"
      />
    </svg>
  );
}

/* ----------------------------------------------------------------------------------------- */

function TopBar() {
  const [crumbRoot, crumbPage] = DIARY_UI.breadcrumb;
  return (
    <Reveal
      cue={{ delay: T.topbar, y: 6 }}
      className="flex h-[6.25rem] items-center gap-[1.4rem] border-b border-white/10 pl-[3.75rem] text-[1.2rem] leading-none text-white/40">
      <PanelRightOpen
        className="h-[2rem] w-[2rem]"
        strokeWidth={1.5}
      />
      <span className="h-[1.1rem] w-px bg-white/15" />
      <span>{crumbRoot}</span>
      <ChevronRight
        className="h-[1.1rem] w-[1.1rem]"
        strokeWidth={1.5}
      />
      <span className="text-white">{crumbPage}</span>
    </Reveal>
  );
}

function Banner() {
  const { banner } = DIARY_UI;
  return (
    <Reveal
      cue={{ delay: T.banner, y: 14, duration: 0.9 }}
      className="absolute inset-x-[3.6rem] top-[8.5rem] flex h-[9.5rem] items-center overflow-hidden rounded-[1rem] border border-white/10 bg-[#0d0804] pl-[5.75rem] pr-[4.1rem] leading-none">
      {/* Static warm base plus a breathing glow layer. */}
      <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,#2a1607_0%,#140a04_100%)]" />
      <span
        className={cn(
          styles.bannerGlow,
          "pointer-events-none absolute inset-0 bg-[radial-gradient(70%_160%_at_38%_0%,rgba(255,120,24,0.55),rgba(255,120,24,0)_70%)]",
        )}
      />
      <motion.span
        variants={reveal}
        custom={{ delay: T.logo, scale: 0.6, duration: 0.8 }}
        className="relative block h-[5.25rem] w-[5.25rem] shrink-0">
        <TradeStatsMark className="h-full w-full" />
      </motion.span>
      <Reveal
        cue={{ delay: T.title, y: 8 }}
        className="relative ml-[4.9rem] flex flex-col gap-[0.95rem]">
        <span className="text-[1.72rem] font-medium text-white">{banner.title}</span>
        <span className="text-[1.375rem] text-white/60">{banner.text}</span>
      </Reveal>
      <Reveal
        cue={{ delay: T.stats, x: 12 }}
        className="relative ml-auto flex items-center gap-[2.5rem]">
        <span className="flex items-end gap-[2rem] text-[1.375rem] font-medium text-white">
          <span className="text-[1.2rem] font-normal text-white/80">{banner.lastLabel}</span>
          {banner.stats.map((s, i) => (
            <span
              key={s}
              className="flex items-end gap-[2rem]">
              {i > 0 && <span className="mb-[0.1rem] h-[1rem] w-px bg-white/20" />}
              {s}
            </span>
          ))}
        </span>
        <span className="flex h-[3.95rem] w-[19.1rem] items-center justify-center gap-[0.7rem] rounded-[0.69rem] bg-white text-[1.2rem] font-medium text-black">
          <Play
            className="h-[1.6rem] w-[1.6rem] fill-black"
            strokeWidth={1.5}
          />
          {banner.button}
        </span>
      </Reveal>
    </Reveal>
  );
}

function CalendarHeading() {
  return (
    <Reveal
      cue={{ delay: T.heading, y: 10 }}
      className="absolute left-[3.6rem] top-[21.6rem] flex items-center gap-[4.6rem] leading-none">
      <span className="text-[2.06rem] font-medium text-white">{DIARY_UI.pastSections}</span>
      <span className="flex items-center gap-[1.6rem] text-[1.375rem] text-white">
        <ChevronLeft
          className="h-[1.3rem] w-[1.3rem] text-white/70"
          strokeWidth={1.75}
        />
        {DIARY_UI.month}
        <ChevronRight
          className="h-[1.3rem] w-[1.3rem] text-white/70"
          strokeWidth={1.75}
        />
      </span>
    </Reveal>
  );
}

/* ----------------------------------------------------------------------------------------- */

function DayCell({ day }: { day: DiaryDay }) {
  const tone = day.entry ? TONE[day.entry.tone] : null;
  const at = day.order !== undefined ? T.fill + day.order * T.fillStep : 0;
  const muted = !!day.outside;
  const style = tone ? ({ "--tone": tone.rgb } as CSSProperties) : undefined;

  return (
    <div
      style={style}
      className={cn(
        "relative h-[12.8rem] border-l border-t border-white/10 pl-[1.2rem] pt-[1.3rem] leading-none",
        "[&:nth-child(7n)]:border-r",
      )}>
      {/* Entry tint: fades in when the day is "written". Outside-month entries stay neutral. */}
      {day.entry && !muted && (
        <motion.span
          variants={reveal}
          custom={{ delay: at, duration: 0.9 }}
          className="pointer-events-none absolute inset-0 border border-[rgba(var(--tone),0.12)] bg-[linear-gradient(75deg,rgba(var(--tone),0.2)_8%,rgba(var(--tone),0)_100%)]"
        />
      )}
      {day.today && (
        <span
          className={cn(
            styles.todayRing,
            "pointer-events-none absolute -inset-px border border-[rgba(255,144,3,0.6)] shadow-[inset_0_0_2.4rem_rgba(255,144,3,0.18)]",
          )}
        />
      )}

      <span className={cn("relative text-[1.03rem]", muted ? "text-white/40" : "text-white")}>{day.day}</span>

      {day.entry && (
        <>
          <motion.span
            variants={reveal}
            custom={{ delay: at + 0.05, scale: 0.6, duration: 0.6 }}
            className={cn(
              "absolute right-[1.4rem] top-[1.3rem] grid h-[3rem] w-[3.1rem] place-items-center rounded-[0.69rem] border text-[1.2rem] font-medium text-white",
              muted
                ? "border-white/10 bg-white/10"
                : "border-[rgba(var(--tone),0.19)] bg-[linear-gradient(92deg,rgba(var(--tone),0.2)_2%,rgba(var(--tone),0)_109%)]",
            )}>
            {day.entry.score}
          </motion.span>

          {day.today && (
            <motion.span
              variants={reveal}
              custom={{ delay: T_TODAY, scale: 0.7, duration: 0.6 }}
              className="absolute left-[1.2rem] top-[4.8rem] rounded-[0.34rem] bg-white/15 px-[0.5rem] py-[0.43rem] text-[0.86rem] font-medium uppercase text-white/80">
              {DIARY_UI.today}
            </motion.span>
          )}

          <Reveal
            cue={{ delay: at + 0.12, y: 6, duration: 0.6 }}
            className="absolute left-[1.2rem] top-[7.1rem] flex items-end gap-[0.43rem]">
            <span
              style={tone && !muted ? { color: tone.text } : undefined}
              className={cn("text-[1.72rem] font-medium", muted && "text-white/30")}>
              {day.entry.pnl}
            </span>
            <span className="text-[0.86rem] text-white/30">{DIARY_UI.pnlLabel}</span>
          </Reveal>
          <Reveal
            cue={{ delay: at + 0.2, y: 4, duration: 0.6 }}
            className={cn(
              "absolute left-[1.2rem] top-[10.1rem] flex items-center gap-[0.6rem] text-[1.03rem]",
              muted ? "text-white/40" : "text-white/80",
            )}>
            <Clock
              className="h-[1.3rem] w-[1.3rem]"
              strokeWidth={1.5}
            />
            {day.entry.time}
          </Reveal>
        </>
      )}
    </div>
  );
}

function Calendar() {
  return (
    <div className="absolute left-[1.9rem] top-[28rem] w-[124.6rem]">
      <Reveal
        cue={{ delay: T.weekdays, y: 6 }}
        className="grid grid-cols-7 pb-[1.6rem] text-center text-[1.03rem] font-medium leading-none text-white/50">
        {DIARY_UI.weekdays.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </Reveal>
      {DIARY_WEEKS.map((week, r) => (
        <Reveal
          key={week[0].day + r}
          cue={{ delay: T.rows + r * T.rowStep, y: 10, duration: 0.8 }}
          className="grid grid-cols-7 last:border-b last:border-white/10">
          {week.map((day) => (
            <DayCell
              key={day.day}
              day={day}
            />
          ))}
        </Reveal>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------------------------- */

const SCENE_LABEL = "Trade Stats daily diary: a month calendar of journal entries with profit, time and score per day";

function Dashboard() {
  return (
    <Reveal
      cue={{ delay: T.panel, y: 48, duration: 1 }}
      className="absolute left-[8.5rem] top-[9.25rem] h-[120rem] w-[132.5rem] overflow-hidden rounded-tl-[1.5rem] border border-white/10 bg-black">
      <TopBar />
      <Banner />
      <CalendarHeading />
      <Calendar />
    </Reveal>
  );
}

/** Desktop composition, inside the 88rem × 104rem frame. */
export default function DailyDiaryScene({ className }: { className?: string }) {
  return (
    <SceneRoot
      label={SCENE_LABEL}
      sceneClassName={styles.scene}
      className={className}>
      {() => <Dashboard />}
    </SceneRoot>
  );
}

/** Mobile composition: the same dashboard, slightly reduced and cropped by the frame. */
export function DailyDiarySceneMobile({ className }: { className?: string }) {
  return (
    <SceneRoot
      label={SCENE_LABEL}
      sceneClassName={styles.scene}
      className={className}>
      {() => (
        <div className="absolute left-[-5.2rem] top-[-5.4rem] h-full w-full origin-top-left scale-[0.78]">
          <Dashboard />
        </div>
      )}
    </SceneRoot>
  );
}
